<template>
  <div class="container px-0" v-if="$gate.isAdmin()">
    <!-- Toolbar -->
    <div class="d-flex justify-content-end mb-3">
      <button type="button"
        :class="['btn', 'btn-sm', 'fw-bold', stare_incarca_fisiere === 1 ? 'btn-light-warning' : 'btn-light-success']"
        @click="switchMode">
        <i :class="stare_incarca_fisiere === 1 ? 'fa-solid fa-eye fs-6 me-1' : 'fa-solid fa-upload fs-6 me-1'"></i>
        {{ stare_incarca_fisiere === 1 ? 'Vizualizare fișiere' : 'Încărcare fișiere' }}
      </button>
    </div>

    <!-- Date Range Card -->
    <div class="card mb-4" v-if="stare_vizualizare_fisiere === 1">
      <div class="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 class="mb-0">Filtrare după perioadă</h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="applyFilters">
          <!-- Row 1: Date range -->
          <div class="row g-3 align-items-end mb-2">
            <div class="col-12 col-md-3">
              <label class="form-label">Data de la</label>
              <div class="position-relative">
                <input ref="dateFromRef" v-model="dateFrom" type="date" class="form-control pe-5"
                  :max="dateTo || undefined" />
                <button type="button" class="date-picker-btn btn btn-outline-secondary btn-sm"
                  @click="openNativePicker('from')" aria-label="Deschide calendar">
                  📅
                </button>
              </div>
            </div>
            <div class="col-12 col-md-3">
              <label class="form-label">Data până la</label>
              <div class="position-relative">
                <input ref="dateToRef" v-model="dateTo" type="date" class="form-control pe-5"
                  :min="dateFrom || undefined" />
                <button type="button" class="date-picker-btn btn btn-outline-secondary btn-sm"
                  @click="openNativePicker('to')" aria-label="Deschide calendar">
                  📅
                </button>
              </div>
            </div>
          </div>

          <!-- Row 2: Search + Status filters -->
          <div class="row g-3 align-items-end">
            <div class="col-12 col-md-6 col-lg-5">
              <label class="form-label">Caută după nume</label>
              <input v-model.trim="filesFilters.q" type="text" class="form-control"
                placeholder="ex: factura, contract" />
            </div>
            <div class="col-6 col-md-3 col-lg-3 d-flex align-items-center">
              <div class="form-check me-4">
                <input class="form-check-input" type="checkbox" id="filterErrors" v-model="filesFilters.onlyErrors">
                <label class="form-check-label" for="filterErrors">
                  Cu erori
                </label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="filterWarnings" v-model="filesFilters.onlyWarnings">
                <label class="form-check-label" for="filterWarnings">
                  Cu avertizări
                </label>
              </div>
            </div>
            <div class="col-12 col-md-3 col-lg-4 d-flex gap-2">
              <button type="submit" class="btn btn-primary w-100" :disabled="!hasActiveFilters">
                Aplică
              </button>
              <button type="button" class="btn btn-light-secondary fw-bold w-100" @click="resetFilters"
                :disabled="!hasActiveFilters">
                <i class="fa-solid fa-rotate-left me-1"></i>
                Resetare
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Upload Card -->
    <div class="card" v-if="stare_incarca_fisiere === 1">
      <div class="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 class="mb-0">Încărcare documente (multiple)</h5>
      </div>
      <div class="card-body">
        <div class="border rounded-3 p-4 text-center mb-3"
          :class="{ 'border-primary': isDragging, 'opacity-50': uploading || isMonthClosed, 'bg-light text-muted': isMonthClosed }"
          :style="{ pointerEvents: (uploading || isMonthClosed) ? 'none' : 'auto' }"
          @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="onDrop">
          <p class="mb-2" v-if="!isMonthClosed">Trage aici fișierele sau</p>
          <p class="mb-2 text-danger fw-bold" v-else><i class="fa-solid fa-lock me-2"></i>Luna este închisă. Încărcarea
            este dezactivată pentru utilizator (și admin aici).</p>
          <div v-if="!isMonthClosed">
            <label class="btn btn-outline-primary">
              Selectează fișiere
              <input ref="fileInput" type="file" class="d-none" multiple
                accept=".pdf,image/*,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" @change="onFileChange" />
            </label>
          </div>
          <small class="text-muted d-block mt-2">
            Acceptăm PDF, imagini (JPG/PNG/WEBP) și documente Office (DOC/DOCX/XLS/XLSX). Maxim 20MB/fișier.
          </small>
        </div>

        <!-- Period Selection Grid -->
        <div class="period-selector-container mb-4 p-3 bg-light rounded border shadow-sm">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="form-label fw-bold mb-0">Selectați perioada pentru încărcare</label>
            <div v-if="years.length > 1" class="btn-group btn-group-sm">
              <button v-for="y in years" :key="y" type="button"
                :class="['btn', selectedYear === y ? 'btn-primary shadow-sm' : 'btn-outline-secondary']"
                @click="selectedYear = y; onYearChange()">
                {{ y }}
              </button>
            </div>
            <div v-else class="badge bg-secondary px-3 py-2">Anul {{ selectedYear }}</div>
          </div>

          <!-- Months Grid (3x4 or 6x2) -->
          <div class="row g-2">
            <div v-for="m in months" :key="m.id" class="col-4 col-sm-3 col-md-2">
              <button type="button"
                class="btn btn-sm w-100 d-flex flex-column align-items-center py-2 position-relative border-2"
                :class="getMonthBtnClass(m.id)" :disabled="isMonthRangeInvalid(m.id)" @click="selectMonth(m.id)"
                style="height: 60px; justify-content: center;">
                <span class="small fw-bold text-uppercase" :class="selectedMonth === m.id ? 'text-white' : ''">
                  {{ m.name.substring(0, 3) }}
                </span>

                <div class="mt-1" style="font-size: 0.7rem;">
                  <span v-if="isValidatedClosed(m.id)" class="text-danger fw-bold">
                    <i class="fa-solid fa-lock"></i> ÎNCHIS
                  </span>
                  <span v-else-if="selectedMonth === m.id" class="text-white">
                    <i class="fa-solid fa-check"></i>
                  </span>
                  <span v-else-if="isMonthRangeInvalid(m.id)" class="text-muted opacity-50">
                    <i class="fa-solid fa-ban"></i> N/A
                  </span>
                  <span v-else class="text-success small">
                    <i class="fa-solid fa-lock-open"></i> DESCHIS
                  </span>
                </div>
              </button>
            </div>
          </div>

          <!-- Selected Period Status -->
          <div class="mt-3">
            <div
              :class="['alert py-2 px-3 mb-0 small border border-dashed rounded-pill d-flex align-items-center', isMonthClosed ? 'alert-light-danger border-danger' : 'alert-light-success border-success']">
              <i :class="['fa-solid me-2', isMonthClosed ? 'fa-lock text-danger' : 'fa-lock-open text-success']"></i>
              <div class="flex-grow-1">
                <span :class="isMonthClosed ? 'text-danger fw-bold' : 'text-success fw-bold'">
                  {{ isMonthClosed ? 'Luna ' + getMonthName(selectedMonth) + ' este ÎNCHISĂ' : 'Luna ' +
                    getMonthName(selectedMonth) + ' este DESCHISĂ' }}
                </span>
                <span class="ms-2 opacity-75">• {{ monthStatusMessage }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Previews -->
        <div v-if="files.length" class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Fișiere selectate ({{ files.length }})</h6>
            <button class="btn btn-sm btn-light border" @click="clearAll">Șterge toate</button>
          </div>

          <div class="row g-3">
            <div v-for="f in files" :key="f.id" class="col-6 col-md-4 col-lg-3">
              <div class="border rounded-3 p-2 h-100 d-flex flex-column">
                <div
                  class="ratio ratio-1x1 mb-2 bg-light d-flex align-items-center justify-content-center overflow-hidden rounded">
                  <img v-if="f.isImage && f.previewUrl" :src="f.previewUrl" alt="preview" class="img-fluid" />
                  <img v-else-if="f.isPdf && f.thumbUrl" :src="f.thumbUrl" alt="pdf thumbnail" class="img-fluid" />
                  <div v-else-if="f.isPdf && f.thumbPending"
                    class="text-center p-3 small w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                    <div class="spinner-border spinner-border-sm mb-2" role="status"></div>
                    <div>Generez miniatura… (PDF mare?)</div>
                  </div>
                  <div v-else class="text-center p-3 small">
                    <div class="fw-semibold">{{ f.ext.toUpperCase() }}</div>
                    <div class="text-muted">{{ humanSize(f.size) }}</div>
                  </div>
                </div>
                <div class="small flex-grow-1">
                  <div class="fw-semibold text-truncate" :title="f.name">{{ f.name }}</div>
                  <div class="text-muted">{{ friendlyType(f) }}</div>
                </div>
                <div class="mt-2 d-flex gap-2">
                  <a v-if="f.previewUrl || f.isPdf || f.isOffice" href="#"
                    class="btn btn-sm btn-outline-secondary w-100" @click.prevent="openPreview(f.id)">Previzualizare</a>
                  <button class="btn btn-sm btn-outline-danger w-100" @click="removeFile(f.id)">Șterge</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end align-items-center">
          <button class="btn btn-success" :disabled="!files.length || uploading" @click="emitUpload">
            {{ uploading ? 'Se încarcă…' : 'Încarcă fișierele' }}
          </button>
          <div v-if="uploading" class="ms-3 d-flex align-items-center gap-2">
            <div class="spinner-border spinner-border-sm" role="status"></div>
            <small>{{ totalProgress }}%</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal (local selection) -->
    <div v-if="showPreview" class="preview-modal d-flex align-items-center justify-content-center">
      <div class="preview-backdrop" @click="closePreview"></div>
      <div class="preview-content card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <strong class="text-truncate" :title="currentFile?.name">{{ currentFile?.name }}</strong>
          <button class="btn btn-sm btn-light" @click="closePreview">Închide</button>
        </div>
        <div class="card-body p-0">
          <!-- Image preview -->
          <div v-if="currentFile && currentFile.isImage" class="p-3 text-center">
            <img :src="currentFile.previewUrl" alt="preview" class="img-fluid" />
          </div>
          <!-- PDF preview -->
          <div v-else-if="currentFile && currentFile.isPdf" class="pdf-preview">
            <iframe :src="currentFile.previewUrl" title="PDF preview"></iframe>
          </div>
          <!-- Office fallback preview -->
          <div v-else-if="currentFile && currentFile.isOffice" class="p-4">
            <p class="mb-2">Preview-ul direct în browser pentru fișiere Word/Excel este limitat fără un URL public.</p>
            <ul class="small text-muted mb-3">
              <li>Poți <strong>descărca</strong> fișierul pentru a-l deschide local.</li>
              <li>Sau oferă un URL public pentru a folosi Office Web Viewer.</li>
            </ul>
            <div class="d-flex gap-2">
              <a :href="currentFile.previewUrl" download class="btn btn-primary">Descarcă</a>
              <a v-if="currentFile.serverUrl"
                :href="'https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(currentFile.serverUrl)"
                target="_blank" rel="noopener" class="btn btn-outline-secondary">Deschide în Office Web Viewer</a>
            </div>
          </div>
          <!-- Generic fallback -->
          <div v-else class="p-4">
            <p>Acest tip de fișier nu are o previzualizare în browser. Îl poți descărca mai jos.</p>
            <a :href="currentFile?.previewUrl" download class="btn btn-primary">Descarcă</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Vizualizare Fișiere Card -->
    <div class="card" v-if="stare_vizualizare_fisiere === 1">
      <div class="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 class="mb-0">Fișiere încărcate (intrări)</h5>
      </div>
      <div class="card-body">
        <div v-if="loadingFiles" class="p-4 text-center">
          <div class="spinner-border" role="status"></div>
        </div>
        <div v-else>
          <div v-if="!groupedFiles.length" class="p-4 text-center text-muted">
            Nu s-au găsit fișiere.
          </div>

          <div v-else class="d-flex flex-column gap-4">
            <!-- Group per month -->
            <div v-for="grp in groupedFiles" :key="grp.key" class="file-month-group">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0">{{ grp.label }}</h6>
                <small class="text-muted">{{ grp.items.length }} fișier{{ grp.items.length === 1 ? '' : 'e' }}</small>
              </div>

              <!-- Thumbnails grid -->
              <div class="thumb-grid">
                <div v-for="it in grp.items" :key="it.id" class="thumb-card">
                  <div class="thumb-media">
                    <img :src="serverThumbSrc(it)" :alt="it.original_name" class="img-fluid" loading="lazy" />
                    <!-- status overlay -->
                    <div class="thumb-status" :class="statusClass(it)" :title="statusLabel(it)"
                      v-if="typeof it.stare_fisier !== 'undefined'">
                      <i :class="statusIcon(it)"></i>
                    </div>
                  </div>
                  <div class="thumb-body">
                    <div class="thumb-title" :title="it.original_name">{{ it.original_name }}</div>
                    <div class="thumb-meta text-muted">{{ formatDate(it.created_at) }} · {{ humanSize(it.size || 0) }}
                    </div>
                  </div>
                  <div class="thumb-actions">
                    <button class="btn btn-sm btn-light-primary fw-bold" @click="openServerPreview(it)">
                      <i class="fa-solid fa-eye fs-5 me-1"></i>
                      Previzualizare
                    </button>
                    <a class="btn btn-sm btn-light-primary fw-bold"
                      :href="`${apiBase}/download_intrare/${it.id}?firma_id=${firmaId}`">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                      Descarcare
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Server Preview Modal (split layout) -->
      <div v-if="serverPreviewItem" class="preview-modal d-flex align-items-center justify-content-center">
        <div class="preview-backdrop" @click="closeServerPreview"></div>
        <div class="preview-content card shadow-lg">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong class="text-truncate" :title="serverPreviewItem.original_name">{{ serverPreviewItem.original_name
              }}</strong>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-primary fw-bold" @click="updateServerItem"
                :disabled="filechange_form.busy">
                <span v-if="filechange_form.busy" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i class="fa-solid fa-save fs-5 me-1"></i>
                Actualizează stare fișier
              </button>
              <a class="btn btn-sm btn-light-primary fw-bold"
                :href="`${apiBase}/download_intrare/${serverPreviewItem.id}?firma_id=${firmaId}`">
                <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                Descarcare
              </a>
              <button class="btn btn-sm btn-light-secondary fw-bold" @click="closeServerPreview">
                <i class="fa-regular fa-circle-xmark fs-5 me-1"></i>
                Închide
              </button>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="row g-0 preview-split">
              <!-- Left: Preview -->
              <div class="col-12 col-lg-7 preview-split-left"
                :class="{ 'zoom-enabled': serverZoomed, 'dragging': isPreviewDragging }" ref="previewLeft"
                @mousedown="onPreviewDragStart">
                <!-- Image preview -->
                <div v-if="isImageMime(serverPreviewItem.mime)" class="pannable p-3 h-100"
                  :class="{ 'centered': !serverZoomed }">
                  <img ref="previewImg" :src="`${apiBase}/stream_intrare/${serverPreviewItem.id}?firma_id=${firmaId}`"
                    alt="preview" class="preview-image" :class="{ 'is-zoomed': serverZoomed }" :style="previewImgStyle"
                    title="Click pentru zoom (1× / 2× / 3×). Ține apăsat și trage pentru a deplasa." draggable="false"
                    @load="onPreviewImgLoaded" @dragstart.prevent @click="toggleServerZoom($event)"
                    @wheel.prevent="onWheelPreview($event)" />
                </div>
                <!-- PDF preview -->
                <div v-else-if="isPdfMime(serverPreviewItem.mime)" class="pdf-preview h-100">
                  <iframe :src="`${apiBase}/stream_intrare/${serverPreviewItem.id}?firma_id=${firmaId}`"
                    title="PDF preview"></iframe>
                </div>
                <!-- Office fallback -->
                <div v-else-if="isOfficeMime(serverPreviewItem.mime)"
                  class="p-4 h-100 d-flex align-items-center justify-content-center text-center">
                  <div>
                    <p class="mb-2">Preview-ul direct în browser pentru Word/Excel este limitat în modul securizat.</p>
                    <p class="small text-muted mb-3">Poți descărca fișierul pentru a-l deschide local.</p>
                    <a class="btn btn-light-primary fw-bold"
                      :href="`${apiBase}/download_intrare/${serverPreviewItem.id}?firma_id=${firmaId}`">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                      Descarcare
                    </a>
                  </div>
                </div>
                <!-- Generic fallback -->
                <div v-else class="p-4 h-100 d-flex align-items-center justify-content-center text-center">
                  <div>
                    <p>Acest tip de fișier nu are previzualizare. Descarcă pentru vizualizare.</p>
                    <a class="btn btn-light-primary fw-bold"
                      :href="`${apiBase}/download_intrare/${serverPreviewItem.id}?firma_id=${firmaId}`">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                      Descarcare
                    </a>
                  </div>
                </div>
              </div>

              <!-- Right: Details + form (admin edits) -->
              <div class="col-12 col-lg-5 preview-split-right" ref="previewRight">
                <form @submit.prevent="updateServerItem" @keydown="filechange_form.onKeydown($event)">
                  <div class="p-3 p-lg-4">
                    <!-- Open raw file in pop-up -->
                    <div class="text-center mb-3">
                      <button class="btn btn-sm btn-light-primary fw-bold" @click="openPopupPreview(serverPreviewItem)"
                        type="button" title="Deschide fișierul într-un pop-up">
                        <i class="fa-solid fa-up-right-from-square fs-6 me-1"></i>
                        Deschide fișierul
                      </button>
                    </div>

                    <!-- Mutare fișier (Relocated) -->
                    <div class="mb-3 border-bottom pb-3">
                      <div class="text-muted small mb-2">Mută fișierul în altă categorie</div>
                      <div class="d-flex flex-column gap-2">
                        <div class="d-flex gap-2">
                          <multiselect v-model="targetKind" :options="moveOptions.filter(o => o.value !== 'intrari')"
                            label="label" track-by="value" :reduce="opt => opt.value" :searchable="false"
                            :show-labels="false" placeholder="Alege destinația" class="flex-grow-1"
                            :disabled="serverPreviewItem.stare_fisier === 1" />
                          <button type="button" class="btn btn-light-warning fw-bold" @click="mutaFisier"
                            :disabled="!targetKind || movingFile || serverPreviewItem.stare_fisier === 1">
                            <span v-if="movingFile" class="spinner-border spinner-border-sm me-1"></span>
                            Mută
                          </button>
                        </div>
                        <div v-if="serverPreviewItem.stare_fisier === 1" class="text-danger small">
                          <i class="fa-solid fa-lock me-1"></i>
                          Fișierul a fost procesat cu succes și nu mai poate fi mutat.
                        </div>
                      </div>
                    </div>

                    <!-- Nume fișier -->
                    <div class="mb-3">
                      <div class="text-muted small">Nume fișier</div>
                      <div class="fw-semibold text-break">{{ serverPreviewItem.original_name || '—' }}</div>
                    </div>

                    <!-- Stare fișier (badge + multiselect) -->
                    <div class="mb-3">
                      <div class="text-muted small d-flex align-items-center justify-content-between">
                        <span>Stare fișier</span>
                        <span>
                          <span class="badge" :class="statusBadgeClass(serverPreviewItem)">
                            <i :class="statusIcon(serverPreviewItem)" class="me-1"></i>
                            {{ statusLabel(serverPreviewItem) }}
                          </span>
                        </span>
                      </div>
                      <div class="d-flex align-items-center gap-2 mt-2">
                        <multiselect v-model="filechange_form.stare_fisier" :options="stateOptions" label="label"
                          track-by="value" :reduce="opt => opt.value" :searchable="false" :allow-empty="true"
                          :show-labels="false" placeholder="Alege starea" class="flex-grow-1" />
                      </div>
                      <div v-if="filechange_form.errors && filechange_form.errors.has('stare_fisier')"
                        class="text-danger small mt-1" v-html="filechange_form.errors.get('stare_fisier')"></div>
                    </div>

                    <!-- Mărime + Data upload, pe același rând -->
                    <div class="mb-3">
                      <div class="row g-3 align-items-start">
                        <div class="col-12 col-sm-6">
                          <div class="text-muted small">Mărime</div>
                          <div class="fw-semibold">{{ humanSize(serverPreviewItem.size || 0) }}</div>
                        </div>
                        <div class="col-12 col-sm-6">
                          <div class="text-muted small">Data upload</div>
                          <div class="fw-semibold">{{ formatDate(serverPreviewItem.created_at) }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- Lucrat de -->
                    <div class="mb-3">
                      <div class="text-muted small">Lucrat de</div>
                      <div class="fw-semibold">{{ serverPreviewItem.lucrat_de_nume || '—' }}</div>
                    </div>

                    <!-- Alte observații (inline editable) -->
                    <div class="mb-3">
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="text-muted small">Alte observații</div>
                        <button type="button" class="btn btn-sm btn-light border"
                          @click="editObservatii = !editObservatii"
                          :title="editObservatii ? 'Anulează editarea' : 'Editează'">
                          <i class="fa-solid" :class="editObservatii ? 'fa-xmark' : 'fa-pen-to-square'"></i>
                        </button>
                      </div>
                      <div v-if="!editObservatii" class="text-break">
                        {{ serverPreviewItem.alte_observatii || '—' }}
                      </div>
                      <div v-else class="mt-2">
                        <textarea class="form-control" rows="3" v-model.trim="filechange_form.alte_observatii"
                          placeholder="Adaugă observații..."></textarea>
                        <div v-if="filechange_form.errors && filechange_form.errors.has('alte_observatii')"
                          class="text-danger small mt-1" v-html="filechange_form.errors.get('alte_observatii')"></div>
                      </div>
                    </div>

                    <!-- Note (inline editable) -->
                    <div>
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="text-muted small">Note</div>
                        <button type="button" class="btn btn-sm btn-light border" @click="editNote = !editNote"
                          :title="editNote ? 'Anulează editarea' : 'Editează'">
                          <i class="fa-solid" :class="editNote ? 'fa-xmark' : 'fa-pen-to-square'"></i>
                        </button>
                      </div>
                      <div v-if="!editNote" class="text-break">
                        {{ serverPreviewItem.note || '—' }}
                      </div>
                      <div v-else class="mt-2">
                        <textarea class="form-control" rows="3" v-model.trim="filechange_form.note"
                          placeholder="Adaugă note..."></textarea>
                        <div v-if="filechange_form.errors && filechange_form.errors.has('note')"
                          class="text-danger small mt-1" v-html="filechange_form.errors.get('note')"></div>
                      </div>
                    </div>

                    <!-- Formular adăugare factură intrare (lazy) -->
                    <div class="mt-4">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Adăugare factură intrare</h6>
                        <div v-if="existingInvoiceInfo" class="alert alert-info py-2 px-3 mb-0 small fw-bold">
                          <i class="fa-solid fa-circle-info me-1"></i>
                          A fost deja generată o factură pentru acest fișier:
                          Seria {{ existingInvoiceInfo.serie }} Nr. {{ existingInvoiceInfo.numar }} (ID: {{
                            existingInvoiceInfo.id }})
                        </div>
                        <button type="button" class="btn btn-sm btn-light-primary fw-bold" v-if="!showFacturaForm"
                          @click="onClickAdaugaFactura">
                          Adaugă factură
                        </button>
                      </div>

                      <AdaugaFactura_Intrare v-if="showFacturaForm" v-model="facturaForm"
                        v-model:produse="facturaProduse" :serii-facturi="seriiFacturi" :lista-furnizori="listaFurnizori"
                        :valute="valute" :unitati-masura="unitatiMasura" :tipuri-produse="tipuriProduse"
                        :plan-conturi="planConturi" :cote-tva="coteTva" @furnizor-selected="onAdminFurnizorSelected"
                        @serie-selected="onAdminSerieSelected" @open-nomenclator-furnizori="openNomenclatorFurnizori"
                        @add-furnizor="adaugaFurnizorDinMultiselect"
                        :suggested-account="suggestedAccount"
                        :firma-id="firmaId">
                      </AdaugaFactura_Intrare>
                    </div>



                    <!-- Actions -->
                    <div class="mt-4 d-flex justify-content-end gap-2">
                      <button v-if="showFacturaForm" type="button" class="btn btn-success fw-bold"
                        @click="salveazaFacturaIntrareAdmin" :disabled="facturaUtilsLoading">
                        <i class="fa-solid fa-check-double me-1"></i>
                        {{ facturaForm.id ? 'Actualizează factura' : 'Salvează factura' }}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div> <!-- /row -->
          </div>
        </div>
      </div> <!-- /Server Preview Modal -->
    </div>
  </div>

  <!-- Nomenclator Furnizori Modal -->
  <div class="modal fade" id="FurnizorModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable furnizor-modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Nomenclator furnizori</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">

          <!-- FORMULAR ADAUGARE/EDITARE FURNIZOR -->
          <form
            @submit.prevent="editmode_furnizor ? actualizeazaFurnizorAdmin(form_modal_furnizori.c_id) : adaugaFurnizorAdmin()">
            <div v-if="showFormAdaugareFurnizor" class="card mb-4 border-primary">
              <div class="card-header bg-primary text-white">
                {{ editmode_furnizor ? 'Modifică furnizor' : 'Adaugă furnizor nou' }}
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <!-- Row 1 -->
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Denumire</label>
                      <input v-model="form_modal_furnizori.f_denumire" type="text" class="form-control" />
                      <div class="text-muted fs-7">Numele complet.</div>
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">CUI</label>
                      <div class="input-group">
                        <input v-model="form_modal_furnizori.c_cui" type="text" class="form-control"
                          placeholder="CUI" />
                        <button class="btn btn-sm btn-icon btn-light-primary" type="button"
                          @click="verificaCUI(form_modal_furnizori.c_cui)">
                          <i class="fa fa-search"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Reg. Com.</label>
                      <input v-model="form_modal_furnizori.c_reg_com" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Localitate</label>
                      <input v-model="form_modal_furnizori.c_localitate" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Telefon</label>
                      <input v-model="form_modal_furnizori.c_telefon" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Email</label>
                      <input v-model="form_modal_furnizori.c_email" type="email" class="form-control" />
                    </div>
                  </div>

                  <!-- Row 2 -->
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Strada</label>
                      <input v-model="form_modal_furnizori.c_strada" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Cod Postal</label>
                      <input v-model="form_modal_furnizori.c_cod_postal" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Adresa</label>
                      <input v-model="form_modal_furnizori.c_adresa" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Judet</label>
                      <input v-model="form_modal_furnizori.c_judet" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Tara</label>
                      <input v-model="form_modal_furnizori.c_tara" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <!-- SPACER -->
                  </div>

                  <!-- Row 3 -->
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Banca</label>
                      <input v-model="form_modal_furnizori.c_banca" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Cont Bancar</label>
                      <input v-model="form_modal_furnizori.c_cont_banca" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Pers Contact</label>
                      <input v-model="form_modal_furnizori.c_pers_contact" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Delegat</label>
                      <input v-model="form_modal_furnizori.c_delegat" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">CNP Del.</label>
                      <input v-model="form_modal_furnizori.c_delegat_cnp" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <!-- SPACER -->
                  </div>

                  <!-- Row 4 -->
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Serie CI</label>
                      <input v-model="form_modal_furnizori.c_ci_serie" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Numar CI</label>
                      <input v-model="form_modal_furnizori.c_ci_nr" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">CI Eliberat</label>
                      <input v-model="form_modal_furnizori.c_ci_eliberat" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Auto Del.</label>
                      <input v-model="form_modal_furnizori.c_delegat_auto" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Cod Agent</label>
                      <input v-model="form_modal_furnizori.c_cod_agent" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <!-- SPACER -->
                  </div>

                  <!-- Row 5 -->
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Zile Scad.</label>
                      <input v-model="form_modal_furnizori.c_scadenta" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Discount</label>
                      <input v-model="form_modal_furnizori.c_discount" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">Atrib. Fiscal</label>
                      <input v-model="form_modal_furnizori.c_atrib_fiscal" type="text" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-1">
                      <label class="form-label">SCP TVA</label>
                      <input v-model="form_modal_furnizori.c_scp_tva" type="text" class="form-control" />
                    </div>
                  </div>

                </div>
              </div>
              <div class="mt-4 text-end">
                <button type="button" class="btn btn-secondary me-2"
                  @click="showFormAdaugareFurnizor = false; editmode_furnizor = false; form_modal_furnizori.reset()">Anulează</button>
                <button type="submit" class="btn btn-success">
                  {{ editmode_furnizor ? 'Actualizează' : 'Salvează' }}
                </button>
              </div>
            </div>
          </form>


          <div class="row g-3 align-items-end mb-3">
            <div class="col-12 col-md-5">
              <label class="form-label">Caută furnizor</label>
              <input v-model.trim="furnizorModalSearch" type="text" class="form-control"
                placeholder="Caută după nume / CUI / email / telefon" />
            </div>
            <div class="col-12 col-md-7 d-flex justify-content-md-end gap-2">
              <button class="btn btn-success"
                @click="showFormAdaugareFurnizor = !showFormAdaugareFurnizor; form_modal_furnizori.reset(); editmode_furnizor = false"
                v-if="!showFormAdaugareFurnizor">
                <i class="fa fa-plus me-1"></i> Adaugă furnizor
              </button>
              <button type="button" class="btn btn-light" @click="furnizorModalSearch = ''">
                Reset
              </button>
              <button type="button" class="btn btn-primary" @click="refreshFurnizoriForModal">
                Reîncarcă
              </button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-hover align-middle mb-0 text-nowrap">
              <thead class="table-light fw-bold">
                <tr>
                  <th class="min-w-30px">ID</th>
                  <th class="min-w-150px">Denumire</th>
                  <th class="min-w-50px">CUI</th>
                  <th class="min-w-80px">Reg.Com</th>
                  <th class="min-w-20px">Localitate</th>
                  <th class="min-w-100px">Zile scadenta</th>
                  <th class="min-w-80px">Reducere acordata</th>
                  <th class="min-w-70px">Email</th>
                  <th class="min-w-100px">Telefon</th>
                  <th class="min-w-80px">Persoana de contact</th>
                  <th class="min-w-100px text-end">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredFurnizori.length">
                  <td colspan="11" class="text-center text-muted py-4">
                    Nu există furnizori pentru criteriul ales.
                  </td>
                </tr>
                <tr v-for="dc in filteredFurnizori" :key="dc.c_id || dc.id">
                  <td>{{ dc.c_id || dc.id || '' }}</td>
                  <td class="fw-semibold" :title="JSON.stringify(dc)">{{ dc.c_denumire || dc.denumire || dc.text || ''
                    }}</td>
                  <td>{{ dc.c_cui || dc.cui || '' }}</td>
                  <td>{{ dc.c_reg_com || dc.reg_com || '' }}</td>
                  <td>{{ dc.c_localitate || dc.localitate || '' }}</td>
                  <td>{{ dc.c_scadenta || dc.scadenta || '' }}</td>
                  <td>{{ dc.c_discount || dc.discount || '' }}</td>
                  <td>{{ dc.c_email || dc.email || '' }}</td>
                  <td>{{ dc.c_telefon || dc.telefon || dc.tel || '' }}</td>
                  <td>{{ dc.c_pers_contact || dc.pers_contact || '' }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary me-1" @click="editareFurnizorFormFill(dc)"
                      title="Editează">
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="stergeFurnizorAdmin(dc.c_id || dc.id)"
                      title="Șterge">
                      <i class="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Închide</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs'
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'

import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import AdaugaFactura_Intrare from '../facturare_components/AdaugaFactura_Intrare.vue'

if (pdfjsLib && pdfjsLib.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl
}

export default {
  name: 'AdminIncarcariIntrari',
  components: { Multiselect, AdaugaFactura_Intrare },
  props: {
    firmaId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['submit-range'],
  data() {
    return {
      uploading: false,
      totalProgress: 0,

      dateFrom: '',
      dateTo: '',
      isDragging: false,
      files: [], // Array<{ id, file, name, size, type, ext, isImage, isPdf, isOffice, previewUrl, thumbUrl, thumbPending, serverUrl }>
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      months: [
        { id: 1, name: 'Ianuarie' }, { id: 2, name: 'Februarie' }, { id: 3, name: 'Martie' },
        { id: 4, name: 'Aprilie' }, { id: 5, name: 'Mai' }, { id: 6, name: 'Iunie' },
        { id: 7, name: 'Iulie' }, { id: 8, name: 'August' }, { id: 9, name: 'Septembrie' },
        { id: 10, name: 'Octombrie' }, { id: 11, name: 'Noiembrie' }, { id: 12, name: 'Decembrie' }
      ],
      years: [],
      isMonthClosed: false,
      firmCreatedAt: null,
      monthStatuses: [], // Cache for current year
      showPreview: false,
      currentFileId: null,
      _pdfQueue: [],
      _pdfActive: 0,
      _pdfMax: 3,
      _pdfStarted: false,

      // Modes
      stare_incarca_fisiere: 0,
      stare_vizualizare_fisiere: 1,
      loadingFiles: false,
      filesList: [],
      filesFilters: {
        q: '',
        per_page: 20,
        page: 1,
        onlyErrors: false,
        onlyWarnings: false,
      },
      serverPreviewItem: null,
      serverZoomed: false,
      serverZoomScale: 1,
      serverZoomLevel: 0,         // 0: 1x, 1: 2x, 2: 3x
      zoomScales: [1, 2, 3],
      isPreviewDragging: false,
      previewDragStartX: 0,
      previewDragStartY: 0,
      previewScrollStartLeft: 0,
      previewScrollStartTop: 0,
      suppressNextClick: false,
      movedDuringDrag: false,
      serverThumbs: {}, // { [id]: { src: string, pending: boolean } }
      serverImgNaturalW: 0,
      serverImgNaturalH: 0,

      stateOptions: [
        { value: 0, label: 'În așteptare' },
        { value: 1, label: 'Succes' },
        { value: 2, label: 'Eșec' },
        { value: 3, label: 'Avertizare' },
      ],
      selectedState: null,
      savingState: false,
      targetKind: null,
      movingFile: false,
      moveOptions: [
        { value: 'intrari', label: 'Intrări' },
        { value: 'iesiri', label: 'Ieșiri' },
        { value: 'extrase', label: 'Extrase' },
        { value: 'altedocts', label: 'Alte documente' },
      ],
      filechange_form: Form.make({
        id: '',
        stare_fisier: { label: 'În așteptare', value: 0 },
        alte_observatii: '',
        note: '',
        firma_id: null,
        tip_stoc: null,
      }),

      // --- Nomenclator Furnizori ---
      showFormAdaugareFurnizor: false,
      editmode_furnizor: false,
      form_modal_furnizori: Form.make({
        c_id: null,
        f_denumire: '',
        c_cui: '',
        c_reg_com: '',
        c_localitate: '',
        c_telefon: '',
        c_email: '',
        c_strada: '',
        c_cod_postal: '',
        c_adresa: '',
        c_judet: '',
        c_tara: '',
        c_banca: '',
        c_cont_banca: '',
        c_pers_contact: '',
        c_delegat: '',
        c_delegat_cnp: '',
        c_ci_serie: '',
        c_ci_nr: '',
        c_ci_eliberat: '',
        c_delegat_auto: '',
        c_cod_agent: '',
        c_scadenta: '',
        c_discount: '',
        c_atrib_fiscal: '',
        c_scp_tva: '',
        c_cut: '', // cut?

        // extra
        id_firma: null
      }),
      editObservatii: false,
      editNote: false,

      // Nomenclator Furnizori modal
      furnizorModalSearch: '',

      // Formular factură intrare (vertical)
      showFacturaForm: false,
      facturaUtilsLoading: false,
      facturaUtilsLoaded: false,
      facturaForm: {
        id_furnizor: null,
        furnizor_obj: null,
        serie_document: null,
        serie_document_obj: null,
        nr_document: '',
        data_emitere: '',
        termen_plata: '',
        valuta: 'RON',
        valuta_obj: null,
        curs_valutar: 1,
        delegat_nume: '',
        delegat_date: '',
        delegat_auto: '',
        intocmit_de: '',
        intocmit_cnp: '',
        informatii_suplimentare: '',
        sold: 0,
      },
      facturaProduse: [],
      suggestedAccount: null,
      seriiFacturi: [],
      listaFurnizori: [],
      valute: [],
      unitatiMasura: [],
      tipuriProduse: [],
      planConturi: [],
      coteTva: [],
    }
  },
  computed: {
    currentFile() {
      return this.files.find(f => f.id === this.currentFileId) || null
    },
    filteredMonths() {
      if (!this.firmCreatedAt) return this.months
      const start = new Date(this.firmCreatedAt)
      const startYear = start.getFullYear()
      const startMonth = start.getMonth() + 1
      const now = new Date()
      const maxDate = new Date(now.getFullYear(), now.getMonth() + 4, 1)
      const maxYear = maxDate.getFullYear()
      const maxMonth = maxDate.getMonth() + 1

      return this.months.filter(m => {
        if (this.selectedYear === startYear && m.id < startMonth) return false
        if (this.selectedYear === maxYear && m.id > maxMonth) return false
        return true
      })
    },
    monthStatusMessage() {
      return this.isMonthClosed ? 'Luna este închisă. Încărcarea este dezactivată pentru utilizator (și admin aici).' : 'Luna este deschisă. Se pot face înregistrări.'
    },
    groupedFiles() {
      const groups = {}
      for (const it of this.filesList) {
        const key = this._extractMonthKey(it) || this._monthKeyFromDate(it.created_at)
        if (!groups[key]) groups[key] = { key, label: this._labelFromMonthKey(key), items: [] }
        groups[key].items.push(it)
      }
      const arr = Object.values(groups)
      arr.sort((a, b) => {
        const [am, ay] = a.key.split('_').map(Number)
        const [bm, by] = b.key.split('_').map(Number)
        if (ay !== by) return by - ay
        return bm - am
      })
      return arr
    },
    hasActiveFilters() {
      const hasDates = !!(this.dateFrom || this.dateTo)
      const hasText = !!(this.filesFilters.q && this.filesFilters.q.trim() !== '')
      const hasFlags = !!(this.filesFilters.onlyErrors || this.filesFilters.onlyWarnings)
      return hasDates || hasText || hasFlags
    },
    previewImgStyle() {
      if (!this.serverZoomed) {
        return {
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain'
        }
      }
      const nW = this.serverImgNaturalW || 0
      const scale = this.serverZoomScale || 1
      if (nW > 0) {
        return {
          width: (nW * scale) + 'px',
          maxWidth: 'none',
          height: 'auto',
        }
      }
      const pct = Math.max(100, Math.min(300, Math.round(scale * 100)))
      return { width: pct + '%', maxWidth: 'none', height: 'auto' }
    },
    apiBase() {
      return '/api/administrare/work/fisiere'
    },

    filteredFurnizori() {
      const list = Array.isArray(this.listaFurnizori) ? this.listaFurnizori : []
      const q = (this.furnizorModalSearch || '').toLowerCase().trim()
      if (!q) return list

      return list.filter(f => {
        const name = this.furnizorLabelSafe(f).toLowerCase()
        const cui = String(f.c_cui || f.cui || f.CUI || f.cif || f.cod_fiscal || '').toLowerCase()
        const email = String(f.c_email || f.email || '').toLowerCase()
        const tel = String(f.c_telefon || f.telefon || f.tel || '').toLowerCase()
        return name.includes(q) || cui.includes(q) || email.includes(q) || tel.includes(q)
      })
    },
    existingInvoiceInfo() {
      if (!this.serverPreviewItem || !this.serverPreviewItem.note) return null
      const note = this.serverPreviewItem.note
      // Standard format: [Factura: Serie XXX, Numar YYY, ID ZZZ]
      const matchNew = note.match(/\[Factura: Serie (.*?), Numar (.*?), ID (.*?)\]/i)
      if (matchNew) {
        return { serie: matchNew[1], numar: matchNew[2], id: matchNew[3] }
      }
      // Fallback for old format - permissive regex for "ID : 123", "ID intrare: 123", etc.
      const matchOld = note.match(/ID\D*?:\s*(\d+)/i)
      if (matchOld) {
        return { serie: '?', numar: '?', id: matchOld[1] }
      }
      return null
    }
  },
  watch: {
    showPreview(val) {
      if (val) {
        this.fetchMissingInvoiceDetails()
      }
    },
    serverPreviewItem(val) {
      if (val) {
        this.fetchMissingInvoiceDetails()
      }
    }
  },
  mounted() {
    this.initDateSelection()
    // când se închide nomenclatorul de furnizori, revino la modalul de factură
    try {
      $('#FurnizorModal')
        .off('hidden.bs.modal.returnToFactura')
        .on('hidden.bs.modal.returnToFactura', () => {
          $('#ModalFacturaIntrare').modal('show')
        })
    } catch (e) {
      // ignore
    }
  },
  beforeUnmount() {
    this.files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl) })
  },
  methods: {
    async initDateSelection() {
      await this.checkMonthStatus() // This will also fetch firmCreatedAt
      this.generateYears()
      await this.loadFacturaUtils()
      if (this.stare_vizualizare_fisiere === 1) {
        await this.fetchFiles(1)
      }
    },
    generateYears() {
      const now = new Date()
      const maxYear = new Date(now.getFullYear(), now.getMonth() + 4, 1).getFullYear()
      let startYear = now.getFullYear() - 1
      if (this.firmCreatedAt) {
        startYear = new Date(this.firmCreatedAt).getFullYear()
      }
      const y = []
      for (let i = startYear; i <= maxYear; i++) y.push(i)
      this.years = y
    },
    async checkMonthStatus() {
      if (!this.firmaId) return
      try {
        const { data } = await axios.get('/api/administrare/work/fisiere/status_luni', {
          params: { an: this.selectedYear, firma_id: this.firmaId }
        })
        this.monthStatuses = data.data || []
        if (data.firm_created_at) {
          this.firmCreatedAt = data.firm_created_at
          if (this.years.length === 0) this.generateYears()
        }
        const s = this.monthStatuses.find(x => x.luna === this.selectedMonth)
        this.isMonthClosed = (s && s.status === 3)
      } catch (e) {
        console.error('Error checking month status:', e)
      }
    },
    onYearChange() {
      this.checkMonthStatus()
      // Adjust month if it becomes invalid for the new year range
      if (this.isMonthRangeInvalid(this.selectedMonth)) {
        // Find first valid month
        for (let i = 1; i <= 12; i++) {
          if (!this.isMonthRangeInvalid(i)) {
            this.selectedMonth = i
            this.checkMonthStatus()
            break
          }
        }
      }
      if (this.stare_vizualizare_fisiere === 1) {
        this.fetchFiles(1)
      }
    },
    selectMonth(id) {
      if (this.isMonthRangeInvalid(id)) return
      this.selectedMonth = id
      this.checkMonthStatus()
    },
    isMonthRangeInvalid(monthId) {
      if (!this.firmCreatedAt) return false
      const start = new Date(this.firmCreatedAt)
      const startYear = start.getFullYear()
      const startMonth = start.getMonth() + 1
      const now = new Date()
      const maxDate = new Date(now.getFullYear(), now.getMonth() + 4, 1)
      const maxYear = maxDate.getFullYear()
      const maxMonth = maxDate.getMonth() + 1

      if (this.selectedYear === startYear && monthId < startMonth) return true
      if (this.selectedYear === maxYear && monthId > maxMonth) return true
      if (this.selectedYear < startYear || this.selectedYear > maxYear) return true

      return false
    },
    isValidatedClosed(monthId) {
      const s = this.monthStatuses.find(x => x.luna === monthId)
      return (s && s.status === 3)
    },
    getMonthBtnClass(monthId) {
      const isSelected = this.selectedMonth === monthId
      const isLocked = this.isValidatedClosed(monthId)
      const isInvalid = this.isMonthRangeInvalid(monthId)

      if (isInvalid) return 'btn-light opacity-25 border-light'
      if (isLocked) return isSelected ? 'btn-danger shadow-sm border-danger' : 'btn-outline-danger'
      if (isSelected) return 'btn-primary shadow-sm border-primary'
      return 'btn-outline-success'
    },
    getMonthName(id) {
      const m = this.months.find(x => x.id === id)
      return m ? m.name : ''
    },
    applyFilters() {
      if (!this.hasActiveFilters) return
      if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
        const tmp = this.dateFrom
        this.dateFrom = this.dateTo
        this.dateTo = tmp
      }
      this.emitSubmit()
      this.fetchFiles(1)
    },
    openNativePicker(which) {
      try {
        const el = which === 'from' ? this.$refs.dateFromRef : this.$refs.dateToRef
        if (!el) return
        if (typeof el.showPicker === 'function') {
          el.showPicker()
        } else {
          el.focus()
          el.click && el.click()
        }
      } catch { }
    },
    switchMode() {
      if (this.stare_incarca_fisiere === 1) {
        this.stare_incarca_fisiere = 0
        this.stare_vizualizare_fisiere = 1
        this.fetchFiles(1)
      } else {
        this.stare_incarca_fisiere = 1
        this.stare_vizualizare_fisiere = 0
      }
    },
    async fetchMissingInvoiceDetails() {
      const info = this.existingInvoiceInfo
      if (!info || (info.serie !== '?' && info.numar !== '?')) return

      try {
        const { data } = await axios.get(`/api/administrare/work/fisiere/get_info_factura`, {
          params: { id: info.id, tip: 'intrare' },
          withCredentials: true
        })

        if (data.success && data.data) {
          const { serie, numar, id } = data.data
          const noteMsg = `[Factura: Serie ${serie || ''}, Numar ${numar || ''}, ID ${id}]`

          this.filechange_form.note = noteMsg
          // Auto-save the note to fix it on server - use DB update, send only note to avoid 422 on state object
          try {
            await axios.post(`${this.apiBase}/update_intrare/${this.serverPreviewItem.id}`, {
              firma_id: this.firmaId,
              stare_fisier: 1,
              note: noteMsg
            });
          } catch (e) {
            console.error('Eroare la salvarea automată a notei:', e);
            if (e.response && e.response.data) {
              console.error('Detalii validare:', e.response.data);
            }
          }

          // Sync locally
          this.serverPreviewItem.note = noteMsg
          if (this.serverPreviewItem.id && Array.isArray(this.filesList)) {
            const idx = this.filesList.findIndex(x => x.id === this.serverPreviewItem.id)
            if (idx !== -1) {
              if (this.$set) this.$set(this.filesList[idx], 'note', noteMsg)
              else this.filesList[idx].note = noteMsg
            }
          }
        }
      } catch (err) {
        console.error('Eroare la recuperarea detaliilor facturii:', err)
      }
    },
    async fetchFiles(page = 1) {
      this.loadingFiles = true
      this.filesFilters.page = page
      try {
        const { q, per_page, onlyErrors, onlyWarnings } = this.filesFilters
        const params = { page, per_page }
        if (q) params.q = q

        if (this.dateFrom) params.from = this.dateFrom
        if (this.dateTo) params.to = this.dateTo

        // If no explicit dates, default to selected year
        if (!params.from && !params.to && this.selectedYear) {
          params.from = `${this.selectedYear}-01-01`
          params.to = `${this.selectedYear}-12-31`
        }

        if (onlyErrors) params.only_errors = 1
        if (onlyWarnings) params.only_warnings = 1
        params.firma_id = this.firmaId

        const { data } = await axios.get(`${this.apiBase}/vizualizare_intrari`, { params, withCredentials: true })
        let list = data?.data || []

        // fallback client pentru filtre de stare
        if (onlyErrors || onlyWarnings) {
          list = list.filter(it => {
            const s = Number(it.stare_fisier)
            const isErr = (s === 2)
            const isWarn = (s === 3)
            if (onlyErrors && onlyWarnings) return isErr || isWarn
            if (onlyErrors) return isErr
            if (onlyWarnings) return isWarn
            return true
          })
        }

        this.filesList = list
        for (const it of this.filesList) this.ensureServerThumb(it)
      } catch (e) {
        console.error(e)
        this.filesList = []
      } finally {
        this.loadingFiles = false
      }
    },
    formatDate(s) {
      if (!s) return '-'
      const d = new Date(s)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = String(d.getFullYear())
      return `${dd}/${mm}/${yyyy}`
    },
    openPreview(id) {
      this.currentFileId = id
      this.showPreview = true
    },
    closePreview() {
      this.showPreview = false
      this.currentFileId = null
    },
    resetFilters() {
      if (!this.hasActiveFilters) return
      this.dateFrom = ''
      this.dateTo = ''
      this.filesFilters.q = ''
      this.filesFilters.onlyErrors = false
      this.filesFilters.onlyWarnings = false
      this.fetchFiles(1)
    },
    resetDates() {
      this.dateFrom = ''
      this.dateTo = ''
    },
    emitSubmit() {
      this.$emit('submit-range', { from: this.dateFrom || null, to: this.dateTo || null, filesCount: this.files.length })
    },
    async emitUpload() {
      if (!this.files.length || this.uploading) return
      const fd = new FormData()
      const allowed = /\.(pdf|jpe?g|png|webp|docx?|xlsx?)$/i
      const valid = []
      for (const f of this.files) {
        if (!allowed.test(f.name)) continue
        if (f.size > 20 * 1024 * 1024) continue
        valid.push(f.file)
      }
      if (!valid.length) {
        alert('Nu există fișiere valide de încărcat (accept: PDF/JPG/PNG/WEBP, max 20MB).')
        return
      }
      valid.forEach(file => fd.append('files[]', file))
      fd.append('firma_id', this.firmaId)
      fd.append('luna', this.selectedMonth)
      fd.append('an', this.selectedYear)

      this.uploading = true
      this.totalProgress = 0
      try {
        await axios.post(`${this.apiBase}/upload_intrari`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (pe) => {
            if (!pe.total) return
            this.totalProgress = Math.round((pe.loaded * 100) / pe.total)
          },
          withCredentials: true,
        })
        this.clearAll()
        this.totalProgress = 0
        this.$emit('submit-range', { from: this.dateFrom || null, to: this.dateTo || null, filesCount: 0 })
        alert('Fișierele au fost încărcate cu succes.')
      } catch (e) {
        console.error(e)
        alert('Upload eșuat. Verifică tipurile/dimensiunea fișierelor sau autentificarea.')
      } finally {
        this.uploading = false
      }
    },

    onFileChange(e) {
      const selected = Array.from(e.target.files || [])
      this.addFiles(selected)
      e.target.value = ''
    },
    onDrop(e) {
      this.isDragging = false
      const dropped = Array.from(e.dataTransfer?.files || [])
      this.addFiles(dropped)
    },

    async addFiles(fileList) {
      for (const file of fileList) {
        const allowedExt = /\.(pdf|jpe?g|png|webp|docx?|xlsx?)$/i
        if (!allowedExt.test(file.name)) {
          console.warn('Tip neacceptat:', file.name)
          continue
        }
        if (file.size > 20 * 1024 * 1024) {
          console.warn('Fișier prea mare:', file.name)
          continue
        }
        const id = this.cryptoRandomId()
        const ext = (file.name.split('.').pop() || '').toLowerCase()
        const type = file.type
        const isImage = type?.startsWith('image/') || false
        const isPdf = (type && type.includes('pdf')) || ext === 'pdf'
        const isOffice = ['doc', 'docx', 'xls', 'xlsx'].includes(ext) ||
          type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          type === 'application/msword' ||
          type === 'application/vnd.ms-excel' ||
          type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

        const previewUrl = URL.createObjectURL(file)

        const record = {
          id,
          file,
          name: file.name,
          size: file.size,
          type,
          ext,
          isImage,
          isPdf,
          isOffice,
          previewUrl,
          thumbUrl: null,
          thumbPending: !!isPdf,
          serverUrl: null
        }
        this.files.push(record)

        if (isPdf) {
          this._pdfQueue.push({ file, record })
        }
      }
      this.$nextTick(() => setTimeout(() => {
        if (!this._pdfStarted) this._drainPdfQueue()
        else this._drainPdfQueue()
      }, 0))
    },

    removeFile(id) {
      const idx = this.files.findIndex(f => f.id === id)
      if (idx !== -1) {
        const f = this.files[idx]
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
        this.files.splice(idx, 1)
      }
    },
    clearAll() {
      this.files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl) })
      this.files = []
    },

    friendlyType(f) {
      if (!f) return 'Fișier'
      const ext = (f.ext || '').toLowerCase()
      const mime = (f.type || '').toLowerCase()
      if (f.isImage || mime.startsWith('image/')) {
        if (ext === 'jpg' || ext === 'jpeg') return 'Imagine (JPG)'
        if (ext === 'png') return 'Imagine (PNG)'
        if (ext === 'webp') return 'Imagine (WEBP)'
        return 'Imagine'
      }
      if (f.isPdf || mime.includes('pdf') || ext === 'pdf') {
        return 'Document PDF'
      }
      if (ext === 'docx' || ext === 'doc' || mime.includes('msword') || mime.includes('wordprocessingml.document')) {
        return ext === 'doc' ? 'Document Word (DOC)' : 'Document Word (DOCX)'
      }
      if (ext === 'xlsx' || ext === 'xls' || mime.includes('vnd.ms-excel') || mime.includes('spreadsheetml.sheet')) {
        return ext === 'xls' ? 'Foaie de calcul Excel (XLS)' : 'Foaie de calcul Excel (XLSX)'
      }
      if (ext) return `Fișier ${ext.toUpperCase()}`
      if (mime) {
        if (mime.includes('text/plain')) return 'Fișier text (TXT)'
        if (mime.includes('json')) return 'Fișier JSON'
        if (mime.includes('zip')) return 'Arhivă ZIP'
      }
      return 'Fișier'
    },
    humanSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    cryptoRandomId() {
      try {
        const arr = new Uint32Array(2)
        crypto.getRandomValues(arr)
        return Array.from(arr).map(n => n.toString(16)).join('')
      } catch {
        return Math.random().toString(16).slice(2) + Date.now().toString(16)
      }
    },

    _drainPdfQueue() {
      this._pdfStarted = true
      if (this._pdfActive >= this._pdfMax) return
      while (this._pdfActive < this._pdfMax && this._pdfQueue.length) {
        const job = this._pdfQueue.shift()
        if (!job) break
        this._pdfActive++
        Promise.resolve().then(async () => {
          try {
            const thumb = await this._generatePdfThumbnailFast(job.file)
            job.record.thumbPending = false
            if (thumb) job.record.thumbUrl = thumb
          } catch (e) {
            console.warn('Thumb job failed:', e)
            job.record.thumbPending = false
          } finally {
            this._pdfActive--
            setTimeout(() => this._drainPdfQueue(), 0)
          }
        })
      }
    },

    async _generatePdfThumbnailFast(file) {
      const HARD_TIMEOUT_MS = 4500

      const renderWithOptions = async (opts) => {
        let loadingTask = null
        try {
          const arrayBuffer = await file.arrayBuffer()
          loadingTask = pdfjsLib.getDocument({
            data: arrayBuffer,
            stopAtErrors: true,
            ...opts,
          })

          const renderFirstPage = (async () => {
            const pdf = await loadingTask.promise
            const page = await pdf.getPage(1)
            const initialViewport = page.getViewport({ scale: 1 })
            const maxEdge = Math.max(initialViewport.width, initialViewport.height)
            const targetEdge = 180
            const scale = Math.max(0.1, Math.min(2.0, targetEdge / maxEdge))
            const viewport = page.getViewport({ scale })

            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            canvas.width = Math.ceil(viewport.width)
            canvas.height = Math.ceil(viewport.height)
            await page.render({ canvasContext: ctx, viewport }).promise
            return canvas.toDataURL('image/png')
          })()

          const timeout = new Promise((resolve) => setTimeout(() => resolve('__TIMEOUT__'), HARD_TIMEOUT_MS))
          const result = await Promise.race([renderFirstPage, timeout])

          if (result === '__TIMEOUT__') {
            try { await loadingTask?.destroy() } catch (_) { }
            return { ok: false, reason: 'timeout' }
          }
          return { ok: true, dataUrl: result || null }
        } catch (err) {
          try { await loadingTask?.destroy() } catch (_) { }
          return { ok: false, reason: err?.message || 'error' }
        }
      }

      let attempt = await renderWithOptions({})
      if (attempt.ok) return attempt.dataUrl
      attempt = await renderWithOptions({ disableWorker: true })
      if (attempt.ok) return attempt.dataUrl
      console.warn('PDF thumbnail failed fast-path:', attempt.reason)
      return null
    },

    openServerPreview(it) {
      this.serverPreviewItem = it
      this.filechange_form.reset();
      this.filechange_form.id = it.id;
      const val = Number(it?.stare_fisier ?? 0);
      const found = this.stateOptions.find(o => o.value === val) || { label: 'În așteptare', value: 0 };
      this.filechange_form.stare_fisier = found;
      this.filechange_form.alte_observatii = it?.alte_observatii || ''
      this.filechange_form.note = it?.note || ''
      this.editObservatii = false
      this.editNote = false
      this.serverZoomLevel = 0
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = false
      this.isPreviewDragging = false
      this.suppressNextClick = false
      this.movedDuringDrag = false
      this.serverImgNaturalW = 0
      this.serverImgNaturalH = 0
      this.$nextTick(() => {
        const el = this.$refs.previewLeft
        const right = this.$refs.previewRight
        if (right && typeof right.scrollTop !== 'undefined') {
          right.scrollTop = 0
        }
        if (el) {
          el.scrollTop = 0
          el.scrollLeft = 0
        }
      })
    },
    closeServerPreview() {
      this.serverPreviewItem = null
      this.targetKind = null;
      this.movingFile = false;
      this.serverZoomLevel = 0
      this.serverZoomScale = this.zoomScales[0]
      this.serverZoomed = false
      this.isPreviewDragging = false
      this.detachPreviewDragListeners()
      this.suppressNextClick = false
      this.movedDuringDrag = false

      // închidem și formularul de factură, să nu rămână deschis între fișiere
      this.showFacturaForm = false
    },
    toggleServerZoom(e) {
      if (this.suppressNextClick) return
      const el = this.$refs.previewLeft
      const img = this.$refs.previewImg
      let relX = 0.5, relY = 0.5
      if (img && e && img.getBoundingClientRect) {
        const rect = img.getBoundingClientRect()
        relX = (e.clientX - rect.left) / rect.width
        relY = (e.clientY - rect.top) / rect.height
        relX = Math.max(0, Math.min(1, relX))
        relY = Math.max(0, Math.min(1, relY))
      }
      this.serverZoomLevel = (this.serverZoomLevel + 1) % this.zoomScales.length
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = this.serverZoomLevel > 0
      this.isPreviewDragging = false
      this.$nextTick(() => {
        if (!el || !this.serverZoomed || !this.$refs.previewImg) {
          if (el && !this.serverZoomed) { el.scrollLeft = 0; el.scrollTop = 0 }
          return
        }
        const newImg = this.$refs.previewImg
        const newW = newImg.clientWidth
        const newH = newImg.clientHeight
        const targetLeft = newW * relX - el.clientWidth / 2
        const targetTop = newH * relY - el.clientHeight / 2
        el.scrollLeft = Math.max(0, targetLeft)
        el.scrollTop = Math.max(0, targetTop)
      })
    },
    onWheelPreview(e) {
      if (!this.serverPreviewItem) return
      const el = this.$refs.previewLeft
      const img = this.$refs.previewImg
      let relX = 0.5, relY = 0.5
      if (img && e && img.getBoundingClientRect) {
        const rect = img.getBoundingClientRect()
        relX = (e.clientX - rect.left) / rect.width
        relY = (e.clientY - rect.top) / rect.height
        relX = Math.max(0, Math.min(1, relX))
        relY = Math.max(0, Math.min(1, relY))
      }
      const dir = e.deltaY > 0 ? -1 : 1
      let nextLevel = this.serverZoomLevel + dir
      nextLevel = Math.max(0, Math.min(this.zoomScales.length - 1, nextLevel))
      if (nextLevel === this.serverZoomLevel) return
      this.serverZoomLevel = nextLevel
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = this.serverZoomLevel > 0
      this.$nextTick(() => {
        if (!el || !this.serverZoomed || !this.$refs.previewImg) {
          if (el && !this.serverZoomed) { el.scrollLeft = 0; el.scrollTop = 0 }
          return
        }
        const newImg = this.$refs.previewImg
        const newW = newImg.clientWidth
        const newH = newImg.clientHeight
        const targetLeft = newW * relX - el.clientWidth / 2
        const targetTop = newH * relY - el.clientHeight / 2
        el.scrollLeft = Math.max(0, targetLeft)
        el.scrollTop = Math.max(0, targetTop)
      })
    },
    onPreviewDragStart(e) {
      if (!this.serverZoomed) return
      if (e.button !== 0) return
      const el = this.$refs.previewLeft
      if (!el) return
      e.preventDefault()
      this.isPreviewDragging = true
      this.movedDuringDrag = false
      this.previewDragStartX = e.clientX
      this.previewDragStartY = e.clientY
      this.previewScrollStartLeft = el.scrollLeft
      this.previewScrollStartTop = el.scrollTop
      window.addEventListener('mousemove', this.onPreviewDragMove, { passive: false })
      window.addEventListener('mouseup', this.onPreviewDragEnd, { passive: false })
    },
    onPreviewDragMove(e) {
      if (!this.isPreviewDragging) return
      const el = this.$refs.previewLeft
      if (!el) return
      e.preventDefault()
      const dx = e.clientX - this.previewDragStartX
      const dy = e.clientY - this.previewDragStartY
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.movedDuringDrag = true
      el.scrollLeft = this.previewScrollStartLeft - dx
      el.scrollTop = this.previewScrollStartTop - dy
    },
    onPreviewDragEnd() {
      if (this.isPreviewDragging && this.movedDuringDrag) {
        this.suppressNextClick = true
        setTimeout(() => { this.suppressNextClick = false }, 0)
      }
      this.isPreviewDragging = false
      this.detachPreviewDragListeners()
    },
    detachPreviewDragListeners() {
      window.removeEventListener('mousemove', this.onPreviewDragMove)
      window.removeEventListener('mouseup', this.onPreviewDragEnd)
    },
    isImageMime(m) { return /^image\//i.test(m || '') },
    isPdfMime(m) { return /pdf/i.test(m || '') },
    isOfficeMime(m) { return /(msword|officedocument\.wordprocessingml\.document|vnd\.ms-excel|officedocument\.spreadsheetml\.sheet)/i.test(m || '') },

    _extractMonthKey(it) {
      const p = (it.path || '')
      const m = p.match(/\/(\d{2}_\d{4})\//)
      return m ? m[1] : null
    },
    _monthKeyFromDate(iso) {
      if (!iso) return null
      const d = new Date(iso)
      const mm = (d.getMonth() + 1).toString().padStart(2, '0')
      const yyyy = d.getFullYear().toString()
      return `${mm}_${yyyy}`
    },
    _labelFromMonthKey(key) {
      if (!key) return 'Necunoscut'
      const [mm, yyyy] = key.split('_').map(Number)
      const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie']
      const name = monthNames[(mm - 1) % 12] || mm
      return `${name} ${yyyy}`
    },
    statusClass(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'status-success'
        case 2: return 'status-fail'
        case 3: return 'status-warning'
        default: return 'status-pending'
      }
    },
    statusIcon(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'fa-solid fa-check'
        case 2: return 'fa-solid fa-xmark'
        case 3: return 'fa-solid fa-triangle-exclamation'
        default: return 'fa-solid fa-clock'
      }
    },
    statusLabel(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'Succes'
        case 2: return 'Eșec'
        case 3: return 'Avertizare'
        default: return 'În așteptare'
      }
    },
    statusBadgeClass(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'bg-success'
        case 2: return 'bg-danger'
        case 3: return 'bg-warning text-dark'
        default: return 'bg-primary'
      }
    },

    serverThumbSrc(it) {
      const entry = this.serverThumbs[it.id]
      if (entry && entry.src) return entry.src
      this.ensureServerThumb(it)
      return this._placeholderSvgDataUrl(it)
    },
    async ensureServerThumb(it) {
      if (this.serverThumbs[it.id]?.pending || this.serverThumbs[it.id]?.src) return
      this.$set ? this.$set(this.serverThumbs, it.id, { src: '', pending: true }) : (this.serverThumbs[it.id] = { src: '', pending: true })
      try {
        if (this.isImageMime(it.mime)) {
          const src = `${this.apiBase}/stream_intrare/${it.id}?firma_id=${this.firmaId}`
          this.serverThumbs[it.id].src = src
        } else if (this.isPdfMime(it.mime)) {
          const url = `${this.apiBase}/stream_intrare/${it.id}`
          const resp = await axios.get(url, {
            responseType: 'arraybuffer',
            withCredentials: true,
            params: { firma_id: this.firmaId }
          })
          const dataUrl = await this._renderPdfThumbFromArrayBuffer(resp.data)
          this.serverThumbs[it.id].src = dataUrl || this._placeholderSvgDataUrl(it)
        } else {
          this.serverThumbs[it.id].src = this._placeholderSvgDataUrl(it)
        }
      } catch (e) {
        console.warn('ensureServerThumb failed for', it.id, e)
        this.serverThumbs[it.id].src = this._placeholderSvgDataUrl(it)
      } finally {
        this.serverThumbs[it.id].pending = false
        this.serverThumbs = { ...this.serverThumbs }
      }
    },
    async _renderPdfThumbFromArrayBuffer(arrayBuffer) {
      try {
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer, stopAtErrors: true })
        const pdf = await loadingTask.promise
        const page = await pdf.getPage(1)
        const initialViewport = page.getViewport({ scale: 1 })
        const maxEdge = Math.max(initialViewport.width, initialViewport.height)
        const targetEdge = 220
        const scale = Math.max(0.1, Math.min(2.0, targetEdge / maxEdge))
        const viewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return null
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        await page.render({ canvasContext: ctx, viewport }).promise
        return canvas.toDataURL('image/png')
      } catch (e) {
        console.warn('PDF client render failed:', e)
        return null
      }
    },
    _placeholderSvgDataUrl(it) {
      const ext = ((it.original_name || '').split('.').pop() || 'file').toUpperCase()
      const title = (it.original_name || '').slice(0, 40)
      const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="420" height="300" viewBox="0 0 420 300">
        <rect width="100%" height="100%" fill="#e9ecef"/>
        <g font-family="system-ui,-apple-system,Segoe UI,Roboto" fill="#495057" text-anchor="middle">
          <text x="210" y="150" font-size="56" font-weight="700">${ext}</text>
          <text x="210" y="190" font-size="14">${title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</text>
        </g>
      </svg>`
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
    },
    onPreviewImgLoaded(e) {
      const img = e?.target
      if (!img) return
      this.serverImgNaturalW = img.naturalWidth || 0
      this.serverImgNaturalH = img.naturalHeight || 0
    },
    openPopupPreview(item) {
      if (!item) return
      const url = `${this.apiBase}/stream_intrare/${item.id}?firma_id=${this.firmaId}`
      const width = 1000
      const height = 800
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      window.open(url, 'PopupPreview', `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`)
    },
    async mutaFisier() {
      if (!this.serverPreviewItem || !this.targetKind) return;

      const targetValue = this.targetKind.value || this.targetKind;
      const targetLabel = this.moveOptions.find(o => o.value === targetValue)?.label || targetValue;

      const result = await Swal.fire({
        title: 'Mutare fișier',
        text: `Sigur vrei să muți acest fișier în categoria "${targetLabel}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Da, mută',
        cancelButtonText: 'Anulează'
      });

      if (!result.isConfirmed) return;

      this.movingFile = true;
      try {
        const payload = {
          file_id: this.serverPreviewItem.id,
          from_kind: 'intrari',
          to_kind: targetValue,
          firma_id: this.firmaId
        };

        await axios.post('/api/administrare/work/fisiere/muta_fisier', payload);

        if (typeof toast !== 'undefined' && toast) {
          toast.fire({
            icon: 'success',
            title: 'Fișierul a fost mutat cu succes!',
            timer: 1500
          });
        }

        // Eliminăm din listă locală și închidem preview
        if (Array.isArray(this.filesList)) {
          this.filesList = this.filesList.filter(x => x.id !== this.serverPreviewItem.id);
        }
        this.closeServerPreview();

      } catch (err) {
        console.error('Eroare la mutarea fișierului:', err);
        const msg = err.response?.data?.message || 'Eroare la mutare.';
        if (typeof toast !== 'undefined' && toast) {
          toast.fire({ icon: 'error', title: msg });
        }
      } finally {
        this.movingFile = false;
      }
    },
    async updateServerItem() {
      if (!this.serverPreviewItem) return
      this.$Progress.start();
      try {
        this.filechange_form.firma_id = this.firmaId
        if (this.filechange_form.stare_fisier && typeof this.filechange_form.stare_fisier === 'object') {
          this.filechange_form.stare_fisier = this.filechange_form.stare_fisier.value;
        }
        await this.filechange_form.post(`${this.apiBase}/update_intrare/${this.serverPreviewItem.id}`)
        toast.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Fișierul a fost actualizat cu succes!',
          showConfirmButton: false,
          timer: 1200
        });
        this.$Progress.finish();

        // sincronizează UI-ul local
        this.serverPreviewItem.stare_fisier = Number(this.filechange_form.stare_fisier)
        const idx = this.filesList.findIndex(x => x.id === this.serverPreviewItem.id)
        if (idx !== -1) {
          const updated = { ...this.filesList[idx], stare_fisier: Number(this.filechange_form.stare_fisier) }
          if (this.$set) this.$set(this.filesList, idx, updated)
          else this.filesList.splice(idx, 1, updated)
        }
      } catch (e) {
        console.error(e);
        const errMsg = e?.response?.data?.message || e.message || 'Actualizarea a eșuat.';
        toast.fire({
          position: 'top-end',
          icon: 'error',
          title: `Eroare: ${errMsg}`,
          showConfirmButton: false,
          timer: 2500
        });
        this.$Progress.fail();
      }
    },

    // === FACTURĂ INTRARE DIN PREVIEW ===
    async loadFacturaUtils() {
      if (this.facturaUtilsLoaded || this.facturaUtilsLoading) return;
      this.facturaUtilsLoading = true;
      try {
        const idFirma = this.firmaId;

        const [furnizoriRes, valuteRes, umRes, pcRes, tvaRes, tipuriRes] = await Promise.all([
          axios.get(`/api/administrare/work/utils/furnizori/${idFirma}`, { withCredentials: true }),
          axios.get(`/api/administrare/work/utils/valute`, { withCredentials: true }),
          axios.get(`/api/administrare/work/utils/unitati-masura`, { withCredentials: true }),

          // Cont Credit (plan conturi)
          axios.get(`/api/administrare/work/utils/plan-conturi/${idFirma}`, { withCredentials: true }),

          // TVA cote
          axios.get(`/api/administrare/work/utils/cote-tva`, { withCredentials: true }),

          // Tipuri produse (optional)
          axios.get(`/api/administrare/work/utils/tipuri-produse`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);

        this.listaFurnizori = furnizoriRes.data || [];
        this.valute = valuteRes.data || [{ id: 'RON', text: 'RON' }];
        this.unitatiMasura = umRes.data || [];

        this.planConturi = pcRes.data || [];
        this.coteTva = (tvaRes.data || []).map(x => ({ ...x, cota_tva: Number(x.cota_tva) }));
        this.tipuriProduse = tipuriRes.data || [];

        // pentru intrări, momentan nu folosim serii
        this.seriiFacturi = [];

        this.facturaUtilsLoaded = true;
      } catch (e) {
        console.error('Eroare loadFacturaUtils (intrări):', e);
        toast.fire({
          position: 'top-end',
          icon: 'error',
          title: 'Nu s-au putut încărca setările pentru factură (intrări).',
          showConfirmButton: false,
          timer: 2500
        });
      } finally {
        this.facturaUtilsLoading = false;
      }
    },

    async onClickAdaugaFactura() {
      // Load utils if needed
      try {
        if (!this.facturaUtilsLoaded) {
          await this.loadFacturaUtils()
        }
      } catch (e) {
        console.error('Eroare la încărcarea utilitarelor de facturare', e)
        this.$swal.fire('Eroare', 'Nu s-au putut încărca datele necesare.', 'error')
        return
      }

      if (this.existingInvoiceInfo) {
        const result = await this.$swal.fire({
          title: 'Factura există deja',
          text: "A fost deja generată o factură pentru acest fișier. Ce dorești să faci?",
          icon: 'info',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonColor: '#3085d6',
          denyButtonColor: '#17a2b8',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Editează existentă',
          denyButtonText: 'Adaugă alta nouă',
          cancelButtonText: 'Anulează'
        })

        if (result.isConfirmed) {
          await this.loadExistingInvoiceIntrare(this.existingInvoiceInfo.id)
          return
        } else if (result.isDenied) {
          // Continue to add new (reset form)
          this.resetFacturaForm()
        } else {
          return
        }
      } else {
        this.resetFacturaForm()
      }

      this.showFacturaForm = true

      // focus pe partea dreaptă
      this.$nextTick(() => {
        const pane = this.$refs.previewRight
        if (pane && typeof pane.scrollTop !== 'undefined') {
          pane.scrollTop = 0
        }
      });
    },

    resetFacturaForm() {
      const baza = {
        id_furnizor: null,
        furnizor_obj: null,
        serie_document: null,
        serie_document_obj: null,
        nr_document: '',
        data_emitere: '',
        termen_plata: '',
        valuta: 'RON',
        valuta_obj: null,
        curs_valutar: 1,
        delegat_nume: '',
        delegat_date: '',
        delegat_auto: '',
        intocmit_de: '',
        intocmit_cnp: '',
        informatii_suplimentare: '',
        sold: 0,
      };

      if (this.serverPreviewItem) {
        if (this.serverPreviewItem.data_document) {
          baza.data_emitere = this.serverPreviewItem.data_document.slice(0, 10);
        }
        if (this.serverPreviewItem.original_name) {
          baza.informatii_suplimentare = `Creată din fișier: ${this.serverPreviewItem.original_name}`;
        }
      }

      this.facturaForm = { ...baza };
      this.facturaProduse = [];
    },

    async loadExistingInvoiceIntrare(id) {
      this.facturaUtilsLoading = true
      try {
        const { data } = await axios.get(`/api/administrare/work/facturi/intrare/detalii/${id}`, {
          params: { firma_id: this.firmaId }
        })
        if (data.status === 'success') {
          const f = data.factura
          this.facturaForm = {
            id: f.id,
            serie_document: f.serie_doc,
            serie_document_obj: this.seriiFacturi.find(s => s.serie === f.serie_doc),
            nr_document: f.nr_doc,
            data_emitere: f.data_doc,
            termen_plata: f.data_scadenta,
            id_furnizor: f.id_furnizor,
            furnizor_obj: this.listaFurnizori.find(c => c.id === f.id_furnizor),
            valuta: f.id_valuta || 'RON',
            valuta_obj: this.valute.find(v => v.id === f.id_valuta),
            curs_valutar: f.curs || 1,
            delegat_nume: f.delegat,
            delegat_date: f.delegat_cnp,
            delegat_auto: f.delegat_auto,
            intocmit_de: f.nume,
            intocmit_cnp: f.cnp,
            informatii_suplimentare: f.informatii_suplimentare,
          }
          this.facturaProduse = data.produse.map(p => ({
            id: p.id,
            pozitie: p.pozitie,
            tip: p.tip_stoc,
            id_produs: p.id_produs,
            denumire: p.denumire,
            cota_tva: p.cota_tva,
            um: p.unitate_masura,
            cantitate: p.cantitate,
            pret: p.pret_unitar,
            valoare: p.valoare,
            valoare_tva: p.tva,
            total: p.total,
            cont_credit: p.cont_credit
          }))
          this.showFacturaForm = true
        }
      } catch (e) {
        console.error('Error loading invoice:', e)
        this.$swal.fire('Eroare', 'Nu s-a putut încărca factura.', 'error')
      } finally {
        this.facturaUtilsLoading = false
      }
    },

    async onAdminFurnizorSelected(furnizor) {
      if (!furnizor) {
        this.facturaForm = {
          ...this.facturaForm,
          id_furnizor: null,
          furnizor: null,
          furnizor_obj: null,
          sold: 0,
        }
        this.suggestedAccount = null;
        return
      }

      const id = furnizor.id ?? furnizor.value ?? furnizor.id_furnizor ?? null

      // Fetch suggested account for this supplier
      if (this.firmaId && id) {
        try {
          const res = await axios.get('/api/administrare/work/utils/last-account/intrare', {
            params: {
              firma_id: this.firmaId,
              id_furnizor: id
            }
          });
          if (res.data.status === 'success') {
            this.suggestedAccount = res.data.cont_credit;
          }
        } catch (e) {
          console.error("Error fetching suggested account for supplier:", e);
        }
      }

      // sold (dacă există)
      let soldVal = this.facturaForm?.sold ?? 0
      if (typeof furnizor.sold !== 'undefined') {
        soldVal = furnizor.sold
      } else if (typeof furnizor.sold_furnizor !== 'undefined') {
        soldVal = furnizor.sold_furnizor
      }

      // IMPORTANT: schimbăm referința obiectului ca să se propage sigur în child
      this.facturaForm = {
        ...this.facturaForm,
        id_furnizor: id,
        furnizor: id,
        furnizor_obj: furnizor,
        sold: soldVal,
      }
    },

    onAdminSerieSelected(serie) {
      if (!serie) {
        this.facturaForm.serie_document = null;
        this.facturaForm.serie_document_obj = null;
        return;
      }

      this.facturaForm.serie_document = serie.serie || serie.cod || serie.value || null;
      this.facturaForm.serie_document_obj = serie;

      // dacă avem last_nr/ultimul_numar și nr_document e gol, sugerăm următorul număr
      const last = serie.last_nr ?? serie.ultimul_numar;
      if (!this.facturaForm.nr_document && typeof last !== 'undefined') {
        const num = Number(last);
        if (!Number.isNaN(num)) {
          this.facturaForm.nr_document = String(num + 1);
        }
      }
    },

    async salveazaFacturaIntrareAdmin() {
      if (!this.serverPreviewItem) {
        alert('Nu este selectat niciun fișier pentru care să creezi factura de intrare.');
        return;
      }

      // validări minime
      if (!this.facturaForm.id_furnizor) {
        alert('Selectează un furnizor înainte de salvare.');
        return;
      }
      /*
      if (!this.facturaForm.serie_document) {
        alert('Selectează seria facturii.');
        return;
      }
      */

      if (!this.facturaForm.nr_document) {
        alert('Completează numărul facturii.');
        return;
      }

      // Check for missing Cont Credit in products
      if (this.facturaProduse && this.facturaProduse.length) {
        const missingCC = this.facturaProduse.some(p => !p.cont_credit && !p.cont_credit_obj);
        if (missingCC) {
          const result = await Swal.fire({
            title: 'Cont credit nesetat',
            text: 'Există produse fără Cont Credit setat. Sigur dorești să continui?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Da, salvează așa',
            cancelButtonText: 'Anulează'
          });

          if (!result.isConfirmed) {
            return;
          }
        }
      }

      this.$Progress.start();
      try {
        const produsePayload = (this.facturaProduse || []).map((p, idx) => {
          const cloned = { ...p };

          // normalizăm TIP (tip_stoc / tip_produs): default = prima opțiune
          if (!cloned.tip_stoc) {
            const first = Array.isArray(this.tipuriProduse) ? this.tipuriProduse[0] : null;
            cloned.tip_stoc = first
              ? (first.id ?? first.value ?? first.cod ?? null)
              : null;
          } else if (typeof cloned.tip_stoc === 'object') {
            cloned.tip_stoc = cloned.tip_stoc.id ?? cloned.tip_stoc.value ?? cloned.tip_stoc.cod ?? null;
          }

          // normalizăm UM: dacă este obiect, extragem id/value/cod
          let umValue = cloned.um;
          if (umValue && typeof umValue === 'object') {
            umValue = umValue.id ?? umValue.value ?? umValue.cod ?? null;
          }
          cloned.um = umValue;

          // normalizăm TVA: dacă avem obiect în cota_tva_obj sau cota_tva e obiect
          if (cloned.cota_tva && typeof cloned.cota_tva === 'object') {
            const t = cloned.cota_tva.cota_tva ?? cloned.cota_tva.value ?? cloned.cota_tva.id;
            cloned.cota_tva = (t != null) ? Number(t) : 0;
          }
          if (cloned.cota_tva_obj && typeof cloned.cota_tva_obj === 'object') {
            const t = cloned.cota_tva_obj.cota_tva ?? cloned.cota_tva_obj.value ?? cloned.cota_tva_obj.id;
            if (t != null) cloned.cota_tva = Number(t);
          }

          // normalizăm Cont Credit: dacă este obiect, extragem codul complet
          if (cloned.cont_credit && typeof cloned.cont_credit === 'object') {
            cloned.cont_credit = cloned.cont_credit.cod_complet ?? cloned.cont_credit.cod ?? cloned.cont_credit.value ?? '';
          }
          if (cloned.cont_credit_obj && typeof cloned.cont_credit_obj === 'object') {
            cloned.cont_credit = cloned.cont_credit_obj.cod_complet ?? cloned.cont_credit_obj.cod ?? cloned.cont_credit_obj.value ?? cloned.cont_credit;
          }

          // dacă nu avem poziție, o calculăm automat
          if (cloned.pozitie == null) {
            cloned.pozitie = idx + 1;
          }

          return cloned;
        });

        const payload = {
          firma_id: this.firmaId,
          fisier_id: this.serverPreviewItem.id,
          factura: this.facturaForm,
          produse: produsePayload,
        };

        const { data } = await axios.post(
          '/api/administrare/work/facturi/intrare/adauga',
          payload,
          { withCredentials: true }
        );

        toast.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Factura de intrare a fost salvată.',
          showConfirmButton: false,
          timer: 1800
        });

        // după salvarea facturii, mutăm starea fișierului pe "Succes" și pregătim nota
        if (this.serverPreviewItem) {
          // setăm starea locală pe succes (1)
          this.serverPreviewItem.stare_fisier = 1;
          const idxFile = this.filesList.findIndex(x => x.id === this.serverPreviewItem.id);
          if (idxFile !== -1) {
            const updatedFile = { ...this.filesList[idxFile], stare_fisier: 1 };
            if (this.$set) this.$set(this.filesList, idxFile, updatedFile);
            else this.filesList.splice(idxFile, 1, updatedFile);
          }

          // pregătim opțiunea de stare pentru formularul de fișier
          const successOpt = this.stateOptions.find(o => o.value === 1) || { value: 1, label: 'Succes' };
          this.filechange_form.stare_fisier = successOpt;

          // construim un mesaj implicit pentru Note
          const now = new Date();
          const dd = String(now.getDate()).padStart(2, '0');
          const mm = String(now.getMonth() + 1).padStart(2, '0');
          const yyyy = String(now.getFullYear());
          const idI = data?.id_factura ?? data?.id_intrare ?? null;
          const sI = this.facturaForm.serie_document;
          const nI = this.facturaForm.nr_document;
          const noteMsg = `[Factura: Serie ${sI}, Numar ${nI}, ID ${idI}]`;

          this.filechange_form.note = noteMsg;
          this.editNote = true;

          // Automatizăm salvarea notei pentru a fixa link-ul
          this.$nextTick(async () => {
            try {
              // IMPORTANT: Send explicit integer for stare_fisier, NOT the object from filechange_form
              await axios.post(`${this.apiBase}/update_intrare/${this.serverPreviewItem.id}`, {
                firma_id: this.firmaId,
                stare_fisier: 1,
                note: noteMsg
              });
            } catch (err) {
              console.error('Eroare la salvarea automată a notei:', err);
              if (err.response && err.response.data) {
                console.error('Detalii validare:', err.response.data);
              }
            }
          });
        }

        // reîncarcă fișierele pentru a vedea statusul actualizat
        await this.fetchFiles(this.filesFilters.page || 1);

        this.showFacturaForm = false;

        // dacă backend-ul întoarce stare_fisier/lucrat_de, sincronizăm local
        if (data && data.fisier) {
          const updated = data.fisier;
          const idx = this.filesList.findIndex(x => x.id === updated.id);
          if (idx !== -1) {
            if (this.$set) this.$set(this.filesList, idx, updated);
            else this.filesList.splice(idx, 1, updated);
          }
          if (this.serverPreviewItem && this.serverPreviewItem.id === updated.id) {
            this.serverPreviewItem = { ...this.serverPreviewItem, ...updated };
          }
        }

        this.$Progress.finish();
      } catch (e) {
        console.error('Eroare salvare factură intrare:', e);
        const errMsg = e?.response?.data?.message || e.message || 'Salvarea facturii de intrare a eșuat.';
        toast.fire({
          position: 'top-end',
          icon: 'error',
          title: errMsg,
          showConfirmButton: false,
          timer: 3000
        });
        this.$Progress.fail();
      }
    },

    furnizorLabelSafe(f) {
      if (!f) return ''
      return f.c_denumire || f.denumire || f.text || f.nume || 'Fără nume'
    },


    async refreshFurnizoriForModal() {
      try {
        // dacă nu sunt încă încărcate utilitarele, le încărcăm
        if (!this.facturaUtilsLoaded && !this.facturaUtilsLoading) {
          await this.loadFacturaUtils()
          return
        }

        // reîncarcă doar furnizorii
        const idFirma = this.firmaId
        const res = await axios.get(`/api/administrare/work/utils/furnizori/${idFirma}`, { withCredentials: true })
        this.listaFurnizori = res.data || []
      } catch (e) {
        console.error('refreshFurnizoriForModal error:', e)
      }
    },


    openNomenclatorFurnizori() {
      try {
        // dacă nu avem furnizori încărcați, încercăm rapid să-i încărcăm
        if ((!this.listaFurnizori || !this.listaFurnizori.length) && !this.facturaUtilsLoading) {
          this.refreshFurnizoriForModal()
        }

        // inchide modalul de factura
        $('#ModalFacturaIntrare').modal('hide')

        // deschide nomenclator furnizori
        $('#FurnizorModal').modal('show')
      } catch (e) {
        console.error('openNomenclatorFurnizori error:', e)
      }
    },

    adaugaFurnizorDinMultiselect(search) {
      // Deschide modalul
      this.openNomenclatorFurnizori()
      
      // Activează formularul de adăugare
      this.showFormAdaugareFurnizor = true
      this.editmode_furnizor = false
      
      // Resetează formularul
      this.form_modal_furnizori.reset()
      
      // Dacă search pare a fi CUI (doar cifre, eventual "RO" in fata)
      if (search) {
        let cuiOnly = search.replace(/\D/g, '')
        if (cuiOnly.length >= 2) {
          this.form_modal_furnizori.c_cui = cuiOnly
          this.verificaCUI(cuiOnly)
        } else {
          this.form_modal_furnizori.f_denumire = search
        }
      }
    },

    async verificaCUI(cui) {
      if (!cui) {
        window.toast.fire({
          icon: 'error',
          title: 'Introdu un CUI valid.'
        });
        return;
      }

      try {
        const response = await axios.post('/api/efactura/verifica-cui/furnizor', {
          cui
        });

        if (response.data.status === 'exists') {
          window.toast.fire({
            icon: 'warning',
            title: 'Furnizorul exista deja in baza de date!'
          });
          return;
        }

        if (response.data.status === 'preview' || response.data.status === 'ok') {
          const furnizor = response.data.furnizor || response.data.client;
          this.form_modal_furnizori.f_denumire = furnizor.denumire || '';
          this.form_modal_furnizori.c_cui = furnizor.cui || '';
          this.form_modal_furnizori.c_reg_com = furnizor.reg_com || '';
          this.form_modal_furnizori.c_atrib_fiscal = furnizor.atrib_fiscal || '';
          this.form_modal_furnizori.c_scp_tva = furnizor.scp_tva || '';
          this.form_modal_furnizori.c_localitate = furnizor.localitate || '';
          this.form_modal_furnizori.c_strada = furnizor.strada || '';
          this.form_modal_furnizori.c_cod_postal = furnizor.cod_postal || '';
          this.form_modal_furnizori.c_adresa = furnizor.adresa || '';
          this.form_modal_furnizori.c_judet = furnizor.judet || '';
          this.form_modal_furnizori.c_tara = furnizor.tara || '';
          this.form_modal_furnizori.c_cont_banca = furnizor.cont_banca || '';
          this.form_modal_furnizori.c_telefon = furnizor.telefon || '';

          // Alte câmpuri dacă e cazul
          window.toast.fire({
            icon: 'success',
            title: response.data.message || 'Furnizorul a fost găsit.'
          });
        } else {
          window.toast.fire({
            icon: 'warning',
            title: 'CUI-ul nu a fost găsit în ANAF.'
          });
        }
      } catch (error) {
        console.error(error);

        window.toast.fire({
          icon: 'warning',
          title: 'Eroare la verificarea CUI.'
        });
      }
    },

    async adaugaFurnizorAdmin() {
      this.$Progress.start();
      // Trimitem și id_firma pentru consistență
      this.form_modal_furnizori.id_firma = this.firmaId;

      try {
        const response = await this.form_modal_furnizori.post('/api/administrare/work/utils/furnizori/adauga');
        
        window.toast.fire({
          icon: 'success',
          title: 'Furnizor adăugat cu succes!'
        });
        
        // Păstrăm datele furnizorului nou înainte de reset
        const newFurnizorData = {
          id: response.data?.id || response.data?.furnizor?.id || response.id,
          c_id: response.data?.id || response.data?.furnizor?.id || response.id,
          c_denumire: this.form_modal_furnizori.f_denumire,
          denumire: this.form_modal_furnizori.f_denumire,
          text: this.form_modal_furnizori.f_denumire,
          c_cui: this.form_modal_furnizori.c_cui,
          cui: this.form_modal_furnizori.c_cui,
        };
        
        this.form_modal_furnizori.reset();
        this.showFormAdaugareFurnizor = false;
        this.editmode_furnizor = false;
        
        // Reîncarcă lista de furnizori și așteaptă finalizarea
        await this.refreshFurnizoriForModal();
        
        // Găsește furnizorul nou adăugat în lista actualizată
        const newFurnizorId = newFurnizorData.id || newFurnizorData.c_id;
        let selectedFurnizor = null;
        
        if (newFurnizorId && Array.isArray(this.listaFurnizori)) {
          selectedFurnizor = this.listaFurnizori.find(f => 
            (f.id === newFurnizorId) || 
            (f.c_id === newFurnizorId) || 
            (f.value === newFurnizorId)
          );
        }
        
        // Dacă nu am găsit prin ID, căutăm prin CUI sau denumire
        if (!selectedFurnizor && Array.isArray(this.listaFurnizori)) {
          selectedFurnizor = this.listaFurnizori.find(f => {
            const cuiMatch = newFurnizorData.c_cui && 
              (String(f.c_cui || f.cui || '') === String(newFurnizorData.c_cui));
            const denumireMatch = newFurnizorData.c_denumire && 
              (String(f.c_denumire || f.denumire || f.text || '').toLowerCase() === 
               String(newFurnizorData.c_denumire).toLowerCase());
            return cuiMatch || denumireMatch;
          });
        }
        
        // Selectează furnizorul în formular
        if (selectedFurnizor) {
          this.onAdminFurnizorSelected(selectedFurnizor);
        } else if (newFurnizorId) {
          // Folosim datele salvate dacă nu găsim în listă
          this.onAdminFurnizorSelected(newFurnizorData);
        }
        
        // Închide modalul de nomenclator
        try {
          $('#FurnizorModal').modal('hide');
        } catch (e) {
          console.warn('Nu am putut închide modalul FurnizorModal:', e);
        }
        
        this.$Progress.finish();

      } catch (error) {
        console.error(error);
        this.$Progress.fail();

        window.toast.fire({
          icon: 'error',
          title: 'Eroare la adăugarea furnizorului!'
        });
      }
    },

    actualizeazaFurnizorAdmin(id) {
      if (!id) return;
      this.$Progress.start();

      this.form_modal_furnizori.put(`/api/administrare/work/utils/furnizori/${id}`).then((response) => {
        window.toast.fire({
          icon: 'success',
          title: 'Furnizor actualizat!'
        });
        this.form_modal_furnizori.reset();
        this.showFormAdaugareFurnizor = false;
        this.editmode_furnizor = false;
        this.refreshFurnizoriForModal(); // Reîncarcă lista
        this.$Progress.finish();

      }).catch((error) => {
        console.error(error);
        this.$Progress.fail();

        window.toast.fire({
          icon: 'error',
          title: 'Eroare la actualizarea furnizorului!'
        });
      });
    },

    stergeFurnizorAdmin(id) {
      Swal.fire({
        title: 'Ștergi furnizorul?',
        text: 'Această acțiune este ireversibilă!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Da, șterge',
        cancelButtonText: 'Anulează'

      }).then((result) => {
        if (!result.isConfirmed) return;
        this.$Progress.start();

        axios.delete(`/api/administrare/work/utils/furnizori/${id}`).then(() => {
          window.toast.fire({
            icon: 'success',
            title: 'Furnizor șters cu succes'
          });
          this.refreshFurnizoriForModal();
          this.$Progress.finish();

        }).catch((error) => {
          this.$Progress.fail();
          let msg = 'Eroare la ștergerea furnizorului.';

          if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message;
          }

          Swal.fire('Eroare', msg, 'error');
        });
      });
    },

    editareFurnizorFormFill(furnizor) {
      this.form_modal_furnizori.reset();

      this.form_modal_furnizori.fill({
        c_id: furnizor.c_id || furnizor.id,
        f_denumire: furnizor.c_denumire || furnizor.denumire || furnizor.text || '',
        c_cui: furnizor.c_cui || furnizor.cui || '',
        c_reg_com: furnizor.c_reg_com || furnizor.reg_com || '',
        c_adresa: furnizor.c_adresa || furnizor.adresa || '',
        c_localitate: furnizor.c_localitate || furnizor.localitate || '',
        c_judet: furnizor.c_judet || furnizor.judet || '',
        c_tara: furnizor.c_tara || furnizor.tara || '',
        c_strada: furnizor.c_strada || furnizor.strada || '',
        c_cod_postal: furnizor.c_cod_postal || furnizor.cod_postal || '',
        c_telefon: furnizor.c_telefon || furnizor.telefon || '',
        c_email: furnizor.c_email || furnizor.email || '',
        c_banca: furnizor.c_banca || furnizor.banca || '',
        c_cont_banca: furnizor.c_cont_banca || furnizor.cont_banca || '',
        c_pers_contact: furnizor.c_pers_contact || furnizor.pers_contact || '',
        c_delegat: furnizor.c_delegat || furnizor.delegat || '',
        c_delegat_cnp: furnizor.c_delegat_cnp || furnizor.delegat_cnp || '',
        c_ci_serie: furnizor.c_ci_serie || furnizor.ci_serie || '',
        c_ci_nr: furnizor.c_ci_nr || furnizor.ci_nr || '',
        c_ci_eliberat: furnizor.c_ci_eliberat || furnizor.ci_eliberat || '',
        c_delegat_auto: furnizor.c_delegat_auto || furnizor.delegat_auto || '',
        c_scadenta: furnizor.c_scadenta || furnizor.scadenta || '',
        c_discount: furnizor.c_discount || furnizor.discount || '',
        c_atrib_fiscal: furnizor.c_atrib_fiscal || furnizor.atrib_fiscal || '',
        c_scp_tva: furnizor.c_scp_tva || furnizor.scp_tva || '',
        c_cod_agent: furnizor.c_cod_agent || furnizor.cod_agent || '',
        c_cut: furnizor.c_cut || furnizor.cut || '',
      });
      this.editmode_furnizor = true;
      this.showFormAdaugareFurnizor = true;
    }
  }
}
</script>

<style scoped>
/* Small polish for previews */
.ratio {
  width: 100%;
}

/* Modal preview styles */
.preview-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
}

.preview-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.preview-content {
  position: relative;
  width: 95vw;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
}

/* Make body the scrollable area */
.preview-content .card-body {
  flex: 1 1 auto;
  overflow: hidden;
  max-height: calc(90vh - 60px);
}

/* Images: keep natural height, allow vertical scroll */
.preview-content img {
  max-width: 100%;
  height: auto;
  display: block;
}

/* PDF iframe: take as much height as available inside scroll area */
.pdf-preview iframe {
  width: 100%;
  border: 0;
  min-height: 70vh;
}

/* Split modal layout */
.preview-split {
  height: calc(90vh - 60px);
}

.preview-split-left {
  border-right: 1px solid rgba(0, 0, 0, .1);
  background: #fff;
  overflow: hidden;
}

.preview-split-right {
  background: #fff;
  overflow: auto;
  max-height: calc(90vh - 60px);
}

.preview-split-left,
.preview-split-right {
  height: 100%;
}

[data-bs-theme="dark"] .preview-split-left,
[data-bs-theme="dark"] .preview-split-right {
  background: #121212 !important;
  border-color: rgba(255, 255, 255, .12) !important;
}

/* Thumbnails grid for server files */
.thumb-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.thumb-card {
  border: 1px solid rgba(0, 0, 0, .125);
  border-radius: .5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.thumb-media {
  position: relative;
  background: #f8f9fa;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.thumb-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Status pill overlay on thumbnail */
.thumb-status {
  position: absolute;
  top: .5rem;
  left: .5rem;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .9rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .2);
  border: 1px solid rgba(0, 0, 0, .1);
  z-index: 2;
}

.status-pending {
  background: #0d6efd;
  color: #fff;
}

.status-success {
  background: #198754;
  color: #fff;
}

.status-fail {
  background: #dc3545;
  color: #fff;
}

.status-warning {
  background: #ffc107;
  color: #212529;
  border-color: rgba(0, 0, 0, .2);
}

[data-bs-theme="dark"] .status-warning {
  color: #212529;
}

.thumb-body {
  padding: .5rem .75rem 0;
  font-size: .9rem;
}

.thumb-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.thumb-meta {
  font-size: .8rem;
}

.thumb-actions {
  padding: .5rem .75rem .75rem;
  display: flex;
  gap: .5rem;
}

/* Hide the native calendar icon to avoid white-on-white issues */
input[type="date"]::-webkit-calendar-picker-indicator {
  opacity: 0 !important;
}

input[type="date"] {
  color-scheme: light;
}

/* Custom calendar button inside the input */
.date-picker-btn {
  position: absolute;
  right: .5rem;
  top: 50%;
  transform: translateY(-50%);
  line-height: 1;
  padding: .25rem .45rem;
}

/* Dark theme adjustment only for the custom date picker button */
[data-bs-theme="dark"] .date-picker-btn {
  background-color: #1f1f1f !important;
  color: #e9ecef !important;
  border-color: rgba(255, 255, 255, .15) !important;
}

[data-bs-theme="dark"] .date-picker-btn:hover {
  background-color: #262626 !important;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-bs-theme="light"]) .date-picker-btn {
    background-color: #1f1f1f !important;
    color: #e9ecef !important;
    border-color: rgba(255, 255, 255, .15) !important;
  }

  :root:not([data-bs-theme="light"]) .date-picker-btn:hover {
    background-color: #262626 !important;
  }
}

/* === Dark theme fixes for thumbnail area (local only) === */
[data-bs-theme="dark"] .thumb-card {
  background: #121212 !important;
  border-color: rgba(255, 255, 255, .12) !important;
}

[data-bs-theme="dark"] .thumb-media {
  background: #1a1a1a !important;
}

[data-bs-theme="dark"] .thumb-title {
  color: #e9ecef !important;
}

[data-bs-theme="dark"] .thumb-meta {
  color: rgba(233, 236, 239, .6) !important;
}

/* Image zoom in server preview */
.preview-split-left.zoom-enabled {
  overflow: auto;
  cursor: grab;
  user-select: none;
}

.preview-split-left.dragging {
  cursor: grabbing;
}

.preview-image {
  max-width: 100%;
  height: auto;
  display: block;
  transition: width .15s ease;
  cursor: zoom-in;
  -webkit-user-drag: none;
  user-select: none;
}

.preview-image.is-zoomed {
  cursor: grab;
}

.pannable {
  width: 100%;
  height: 100%;
}

.pannable.centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pannable.centered .preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-split-left.zoom-enabled .pannable {
  display: block;
  min-width: max-content;
  min-height: max-content;
}

@media (max-width: 576px) {
  .preview-content {
    width: 98vw;
    max-width: 98vw;
    max-height: 92vh;
  }

  .preview-content .card-body {
    max-height: calc(92vh - 60px);
  }

  .preview-split {
    height: calc(92vh - 60px);
  }
}

/* Furnizor modal: ~90% din pagină */
.furnizor-modal-dialog {
  max-width: 90vw;
}
</style>
